"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronDown, ShoppingBag, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";

type SubMenuItem = {
  id: number;
  textItem: string;
  lienItem: string;
};

type MenuItem = {
  id: number;
  textMenu: string;
  lienMenu: string;
  sousMenu?: SubMenuItem[];
};

type CTAType = {
  id: number;
  text: string;
  link: string;
};

type LogoType = {
  url: string;
  alternativeText?: string | null;
};

type HeaderProps = {
  menu: MenuItem[];
  logo?: LogoType;
  cta?: CTAType;
};

export default function Header({ menu, logo, cta }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenuId, setOpenSubmenuId] = useState<number | null>(null);
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  useEffect(() => {
    const footer = document.querySelector("footer");

    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      {
        threshold: 0.05,
      }
    );

    observer.observe(footer);

    return () => observer.disconnect();
  }, []);

  const toggleSubmenu = (id: number) => {
    setOpenSubmenuId((prev) => (prev === id ? null : id));
  };

  const isAgencyMenu = (item: MenuItem) => item.textMenu === "Nos agences";

  return (
    <header
      className={`z-50 w-full border-b border-black/5 bg-white backdrop-blur supports-[backdrop-filter]:bg-white ${
        isFooterVisible ? "relative" : "sticky top-0"
      }`}
    >
      {/* Desktop */}
      <div className="container hidden items-center justify-between py-5 lg:flex">
        <Link href="/" className="inline-block shrink-0">
          <Image
            src={logo?.url || ""}
            alt={logo?.alternativeText || "Régalice Pause Dej"}
            width={170}
            height={150}
          />
        </Link>

        <nav className="items-center gap-8 lg:flex">
          {menu.map((item) =>
            item.sousMenu && item.sousMenu.length > 0 ? (
              <div key={item.id} className="group relative">
                <Link
                  href={item.lienMenu}
                  className="flex items-center gap-1 text-[17px] font-medium text-black transition hover:text-pink"
                >
                  {item.textMenu}
                  <ChevronDown className="h-4 w-4" />
                </Link>

                <div className="invisible absolute left-0 top-full z-50 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="min-w-50 rounded-2xl border border-gray-200 bg-white p-2 shadow-lg">
                    <div
                      className={
                        isAgencyMenu(item)
                          ? "max-h-[300px] overflow-y-auto pr-1"
                          : ""
                      }
                    >
                      {item.sousMenu.map((subItem) => (
                        <Link
                          key={subItem.id}
                          href={subItem.lienItem}
                          className="block rounded-xl px-4 py-3 text-sm text-text-body transition hover:bg-pink hover:text-white"
                        >
                          {subItem.textItem}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.id}
                href={item.lienMenu}
                className="text-[17px] font-medium text-black transition hover:text-pink"
              >
                {item.textMenu}
              </Link>
            )
          )}
        </nav>

        <div className="items-center gap-4 lg:flex">
          <Link
            href={process.env.NEXT_PUBLIC_STRAPI_COMMANDE_ENDPOINT || "#"}
            className="inline-flex items-center gap-3 rounded-full bg-yellow px-7 py-4 text-lg font-semibold text-black transition"
          >
            <ShoppingBag className="ml-2 h-5 w-5" />
            {cta?.text}
          </Link>
        </div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden">
        <div className="flex items-center justify-between px-[8%] py-8">
          <Link href="/" className="inline-block shrink-0">
            <Image
              src={logo?.url || ""}
              alt={logo?.alternativeText || "Régalice Pause Dej"}
              width={130}
              height={110}
            />
          </Link>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            className="flex h-10 w-10 items-center justify-center bg-white text-pink"
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav className="bg-violet-light px-[8%] pb-6">
            {menu.map((item) => (
              <div key={item.id}>
                {item.sousMenu?.length ? (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleSubmenu(item.id)}
                      className="flex w-full items-center justify-between border-b border-[#BFD4E8] py-4 text-[15px] font-medium text-black"
                    >
                      {item.textMenu}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          openSubmenuId === item.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {openSubmenuId === item.id && (
                      <div
                        className={`flex flex-col pl-4 pb-2 ${
                          isAgencyMenu(item)
                            ? "max-h-[260px] overflow-y-auto pr-1"
                            : ""
                        }`}
                      >
                        {item.sousMenu.map((subItem) => (
                          <Link
                            key={subItem.id}
                            href={subItem.lienItem}
                            className="py-2 text-sm text-gray-800 hover:text-pink"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {subItem.textItem}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    href={item.lienMenu}
                    className="flex w-full border-b border-gray-100 py-4 text-[15px] font-medium text-black hover:text-pink"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.textMenu}
                  </Link>
                )}
              </div>
            ))}

            <Link
              href={process.env.NEXT_PUBLIC_STRAPI_COMMANDE_ENDPOINT || "#"}
              className="mt-5 flex items-center justify-center gap-3 rounded-full bg-yellow px-6 py-4 text-base font-semibold text-black"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <ShoppingBag className="h-5 w-5" />
              {cta?.text}
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}